import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { RichIcon } from '@ui/components/RichIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CheckIcon } from '@ui/icons/Check.icon'

import { useWindowResize } from '@utils/domUtils'

import { AppMarketQRCode } from '@global/appMarketLink/desktop/AppMarketQRCode'
import { AppMarketLink } from '@global/appMarketLink/mobile/AppMarketLink'

import { TwoFactorAuthForm } from './TwoFactorAuthForm'
import { TwoFactorAuthLocationState, useTwoFactorAuth } from './useTwoFactorAuth'
import { useTwoFactorAuthText } from './useTwoFactorAuthText'

import styles from './TwoFactorAuthPage.module.scss'

export interface Resend2FAError {
  hideResend: boolean
  errorTimer: number
  tooManyInputs: boolean
}

export const TwoFactorAuthPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    twoFactor,
    resendCode,
    handleVerifyCode,
    error,
    status,
    disableResend,
    initialCountdown,
    resendError,
    openRecoveryCodeModal,
    handleTimer,
    stateChecker,
  } = useTwoFactorAuth()
  const { noteText, resendText, errorText } = useTwoFactorAuthText({
    twoFactor,
    initialCountdown,
    resendCode,
    disableResend,
    resendError,
  })

  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const location = useLocation()
  const locationState = location.state as TwoFactorAuthLocationState

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleTimer().startResetTimer(), [resendError.hideResend])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleTimer().startInitialTimer(), [initialCountdown])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stateChecker().effect(), stateChecker().dependants)

  useEffect(() => {
    if (!locationState?.twoFactorAuthData?.twoFactor) {
      navigate('/login')
    }
  }, [locationState, navigate])

  return (
    <div className={styles.topWrapper}>
      <div className={styles.wrapper}>
        {!isMobile && (
          <div className={styles.qrCode}>
            <AppMarketQRCode />
          </div>
        )}
        <div className={styles.inner}>
          <Button.Ghost iconLeft={ArrowIcon.Left} size='md' onClick={() => navigate('/login')}>
            {t('Sign up.Back to Log In')}
          </Button.Ghost>
          <div className={styles.title}>
            <Heading6 medium>{t('Sign up.Two-Factor Authentication')}</Heading6>
            {status === 'success' && (
              <RichIcon
                bg='surface-secondary-high-em'
                icon={{ icon: CheckIcon, color: 'text-white' }}
                shape='circle'
                size='x24'
              />
            )}
          </div>
          <Body1 medium>{t('Sign up.Enter 6-digit code')}</Body1>
          <Body1 className={styles.notice}>{noteText}</Body1>
          <TwoFactorAuthForm
            resendText={resendText}
            errorText={errorText}
            openRecoveryModal={openRecoveryCodeModal}
            statusType={status}
            onSubmit={handleVerifyCode}
            validationError={error}
          />
        </div>
        <AppMarketLink />
      </div>
    </div>
  )
}
